import type { VesselDrawState } from './types';

/**
 * Medium motorboat: an offshore centre console with a hardtop and twin
 * outboards. Cream hull, level sheer, deep-vee bottom. Bow right.
 */
export const drawMotorMedium = ({ palette, night }: VesselDrawState): void => {
  const { ctx } = palette;
  const c = (hex: string): string => palette.colour(hex);

  ctx.beginPath();
  ctx.moveTo(-44, -9);
  ctx.lineTo(-16, -12);
  ctx.quadraticCurveTo(20, -12, 52, -12); // level sheer (bow same height as stern)
  ctx.quadraticCurveTo(57, -11.5, 55, -6); // bow tip rounds down
  ctx.quadraticCurveTo(53, -2, 48, 2); // stem rakes down to forefoot
  ctx.quadraticCurveTo(14, 10, -22, 9); // deep-vee bottom
  ctx.lineTo(-44, 5);
  ctx.closePath();
  const hull = ctx.createLinearGradient(0, -12, 0, 9);
  hull.addColorStop(0, c('F6F4EE'));
  hull.addColorStop(0.5, c('E5E0D4'));
  hull.addColorStop(1, c('B4ADA0')); // cream hull
  ctx.fillStyle = hull;
  ctx.fill();
  ctx.lineWidth = 1.4;
  ctx.strokeStyle = c('8A8475');
  ctx.stroke();

  // chine + gunwale highlight + sheer accent stripe
  ctx.beginPath();
  ctx.moveTo(47, 1);
  ctx.quadraticCurveTo(8, 6, -44, 2);
  ctx.strokeStyle = c('9AA0A0');
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(-44, -9);
  ctx.lineTo(-16, -12);
  ctx.quadraticCurveTo(20, -12, 52, -12);
  ctx.strokeStyle = c('FFFFFF');
  ctx.globalAlpha = 0.6;
  ctx.lineWidth = 1.4;
  ctx.stroke();
  ctx.globalAlpha = 1;
  ctx.beginPath();
  ctx.moveTo(-42, -7);
  ctx.quadraticCurveTo(20, -9.5, 50, -9.5);
  ctx.strokeStyle = c('2C6E8F');
  ctx.lineWidth = 1.4;
  ctx.globalAlpha = 0.8;
  ctx.stroke();
  ctx.globalAlpha = 1;

  // twin outboards (cowl + two legs)
  ctx.fillStyle = c('2E353C');
  ctx.beginPath();
  ctx.moveTo(-44, -6);
  ctx.lineTo(-52, -6);
  ctx.quadraticCurveTo(-55, -6, -55, -2);
  ctx.lineTo(-55, 3);
  ctx.lineTo(-44, 3);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = c('20262C');
  ctx.fillRect(-53, 3, 4, 9);
  ctx.fillRect(-47, 3, 4, 9);

  // centre console: helm pod with leaning post + raked windscreen
  ctx.beginPath();
  ctx.moveTo(-9, -9);
  ctx.lineTo(-8, -18);
  ctx.quadraticCurveTo(-8, -20, -5, -20);
  ctx.lineTo(6, -20);
  ctx.lineTo(11, -14);
  ctx.lineTo(11, -9);
  ctx.closePath();
  const pod = ctx.createLinearGradient(0, -20, 0, -9);
  pod.addColorStop(0, c('EDF1F4'));
  pod.addColorStop(1, c('C4CDD4'));
  ctx.fillStyle = pod;
  ctx.fill();
  ctx.strokeStyle = c('8A98A2');
  ctx.lineWidth = 0.7;
  ctx.stroke();
  ctx.strokeStyle = c('98A4AE');
  ctx.lineWidth = 0.6;
  ctx.beginPath();
  ctx.moveTo(-5, -19.4);
  ctx.lineTo(6, -19.4);
  ctx.stroke(); // dash line
  ctx.fillStyle = c('5A6470');
  ctx.beginPath();
  ctx.moveTo(-8.5, -18);
  ctx.quadraticCurveTo(-8.5, -21, -5, -21);
  ctx.lineTo(-3, -21);
  ctx.lineTo(-3, -18);
  ctx.closePath();
  ctx.fill(); // leaning-post cushion
  ctx.beginPath();
  ctx.moveTo(6, -20);
  ctx.lineTo(12, -24);
  ctx.lineTo(13, -20);
  ctx.lineTo(8, -18.5);
  ctx.closePath(); // raked windscreen
  ctx.fillStyle = c('B7DBF0');
  ctx.globalAlpha = 0.85;
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.strokeStyle = c('8A98A2');
  ctx.lineWidth = 0.6;
  ctx.stroke();

  // hardtop frame tubes (drawn first so the roof caps them)
  ctx.strokeStyle = c('C2CAD1');
  ctx.lineWidth = 1.6;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(8, -20);
  ctx.lineTo(15, -32);
  ctx.stroke(); // forward raked support
  ctx.beginPath();
  ctx.moveTo(-1, -20);
  ctx.lineTo(2, -32);
  ctx.stroke(); // mid support
  ctx.beginPath();
  ctx.moveTo(-6, -19);
  ctx.lineTo(-8, -32);
  ctx.stroke(); // rear support
  ctx.lineCap = 'butt';

  // fibreglass hardtop slab: thickness + slight forward overhang
  ctx.beginPath();
  ctx.moveTo(-11, -32);
  ctx.lineTo(18, -32);
  ctx.quadraticCurveTo(20, -32, 20, -34);
  ctx.lineTo(20, -34.5);
  ctx.quadraticCurveTo(20, -35.5, 18, -35.5);
  ctx.lineTo(-10, -35.5);
  ctx.quadraticCurveTo(-12, -35.5, -12, -34);
  ctx.quadraticCurveTo(-12, -32, -11, -32);
  ctx.closePath();
  const top = ctx.createLinearGradient(0, -35.5, 0, -32);
  top.addColorStop(0, c('F1F4F7'));
  top.addColorStop(1, c('CFD6DC'));
  ctx.fillStyle = top;
  ctx.fill();
  ctx.strokeStyle = c('A7B2BB');
  ctx.lineWidth = 0.7;
  ctx.stroke();
  ctx.strokeStyle = c('FFFFFF');
  ctx.globalAlpha = 0.6;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(-9, -34.8);
  ctx.lineTo(16, -34.8);
  ctx.stroke();
  ctx.globalAlpha = 1;
  ctx.fillStyle = c('2F383F');
  ctx.fillRect(-2, -32, 7, 2.3); // electronics box under the hardtop

  // antennas
  ctx.strokeStyle = c('3A434B');
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(12, -35.5);
  ctx.lineTo(12, -43);
  ctx.stroke();
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.moveTo(-6, -35.5);
  ctx.lineTo(-7, -42);
  ctx.stroke();

  // rocket-launcher rod holders along the aft edge
  ctx.strokeStyle = c('33414B');
  ctx.lineWidth = 0.7;
  ctx.globalAlpha = 0.8;
  ctx.beginPath();
  ctx.moveTo(-8, -35);
  ctx.lineTo(-20, -47);
  ctx.moveTo(-5, -35);
  ctx.lineTo(-15, -48);
  ctx.moveTo(-2, -35);
  ctx.lineTo(-10, -49);
  ctx.stroke();
  ctx.globalAlpha = 1;

  if (night > 0.04) {
    const alpha = Math.min(0.95, night * 1.05);
    palette.navGlow(54, -10, 8, '45,210,95', alpha);
    palette.navGlow(-43, -12, 7, '255,250,235', alpha * 0.9);
    palette.navGlow(12, -44, 9, '255,250,235', alpha);
    ctx.fillStyle = `rgba(255,196,120,${0.5 * night})`;
    ctx.beginPath();
    ctx.arc(9, -16, 3, 0, Math.PI * 2);
    ctx.fill();
  }
};
